import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import type { BranchRef } from '@/bindings/BranchRef'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { backOfficeKeys, restaurantPath } from '@/features/back-office/api'
import { branchHome } from '@/features/back-office/branch-home'
import { useManagedRestaurant } from '@/features/back-office/scope'
import { AddBranchForm } from '@/features/restaurants/add-branch-form'
import { useT } from '@/lib/i18n/translate'
import { request } from '@/lib/api'
import { canManageRestaurant } from '@/lib/roles'

const ROW = 'border-border flex min-h-tap items-center justify-between gap-3 rounded-lg border p-3'
const ENTRY = 'flex min-w-0 flex-1 items-center justify-between gap-3'

/**
 * The branches the caller may enter (F4): for manager and above, the hub between the restaurant
 * and its branches (2026-09-14), where they also add, deactivate and reactivate branches (K2). One link
 * per row, into the role's branch home.
 */
export function RestaurantBranchesPage() {
  const t = useT()
  const queryClient = useQueryClient()
  const { restaurantSlug, restaurantId, context } = useManagedRestaurant()
  const manages = canManageRestaurant(context.role)
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState<string | undefined>()

  // The shell's context is the branch list; the accounts page's scope picker reads its own copy.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: backOfficeKeys.context(restaurantSlug) }),
      queryClient.invalidateQueries({ queryKey: backOfficeKeys.branches(restaurantId) }),
    ])

  return (
    <Page width="md" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">{t('branches.title')}</h1>
        {manages && !adding && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setAdded(undefined)
              setAdding(true)
            }}
          >
            {t('restaurants.addBranch')}
          </Button>
        )}
      </div>
      <Card className="flex flex-col gap-4">
        <p className="text-muted text-sm">{t('branches.hint')}</p>
        {context.branches.length === 0 ? (
          <p className="text-muted text-sm">{t('branches.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {context.branches.map((branch) => {
              const body = (
                <>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{branch.name}</span>
                    {branch.address && <span className="text-muted truncate text-sm">{branch.address}</span>}
                  </span>
                  {branch.deactivated_at && <Badge tone="muted">{t('restaurants.deactivated')}</Badge>}
                </>
              )
              const params = { restaurantSlug, branchSlug: branch.slug }
              const to = branchHome(context.role, branch.deactivated_at != null)
              return (
                <li key={branch.id} className={ROW}>
                  {to ? (
                    <Link to={to} params={params} className={`${ENTRY} hover:underline`}>
                      {body}
                    </Link>
                  ) : (
                    <span className={ENTRY}>{body}</span>
                  )}
                  {manages && <ActivityButtons restaurantId={restaurantId} branch={branch} onDone={refresh} />}
                </li>
              )
            })}
          </ul>
        )}
        {added && (
          <p role="status" className="text-muted text-sm">
            {t('restaurants.branchAdded', { name: added })}
          </p>
        )}
        {adding && (
          <AddBranchForm
            path={restaurantPath(restaurantId, '/branches')}
            siblingSlugs={context.branches.map((branch) => branch.slug)}
            onAdded={(branch) => {
              setAdded(branch.name)
              setAdding(false)
              return refresh()
            }}
            onCancel={() => setAdding(false)}
          />
        )}
      </Card>
    </Page>
  )
}

/**
 * Deactivate needs a second press and reads as destructive (the platform's look); reactivate is a
 * plain button since nothing is lost by it. Both idempotent POSTs; the list refreshes afterward.
 */
function ActivityButtons({ restaurantId, branch, onDone }: { restaurantId: string; branch: BranchRef; onDone: () => Promise<unknown> }) {
  const t = useT()
  const deactivated = branch.deactivated_at != null
  const flip = useMutation({
    mutationFn: () => request<void>(restaurantPath(restaurantId, `/branches/${branch.id}/${deactivated ? 'reopen' : 'close'}`), { method: 'POST' }),
    onSuccess: onDone,
  })

  if (deactivated) {
    return (
      <span className="flex flex-col items-end gap-2">
        <Button size="sm" variant="secondary" loading={flip.isPending} onClick={() => flip.mutate()}>
          {t('restaurants.reactivate')}
        </Button>
        <FormError error={flip.error} />
      </span>
    )
  }
  return (
    <ConfirmButton
      label={t('restaurants.deactivate')}
      variant="danger-ghost"
      confirmLabel={t('restaurants.confirmDeactivate')}
      confirmAriaLabel={t('restaurants.confirmDeactivateOf', { name: branch.name })}
      align="end"
      confirm={() => flip.mutateAsync()}
      error={flip.error}
    />
  )
}
